import $ from 'jquery';
import _ from 'underscore';
import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import '@bingads-webui-help/apex';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { RequestChatSupport } from './ms-chat-support';
import { RequestCallSupport } from './request-call-support';
import { initApexPlatform } from './initialize-apex';

function useDeepMemo(value) {
  const ref = useRef(null);
  if (!_.isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

const SupportModalsInternal = (props) => {
  const {
    supportOptions,
    permissions,
    userContext,
    helpModel,
    i18n,
    isCopilotEnabled,
    currentLocale,
    featureName,
    chatPortalClassName,
    callPortalClassName,
    showRequestCall,
    showRequestChat,
    apexConfig,
    activity,
    onRailViewOpen,
    onRailViewClose,
    onPopupOpen,
    onPopupClose,
    renderApexHelpSuggestions,
    renderSearchInputIcon,
  } = props;

  activity.trace('helpmodel: SupportModals render');
  activity.trace(`helpModel: ${JSON.stringify(helpModel)}, apexConfig: ${JSON.stringify(apexConfig)}, currentLocale: ${currentLocale}`);

  const onHelpPopupOpenWithA11yFix = useCallback((containerNode) => {
    if (onPopupOpen) {
      onPopupOpen(containerNode);
    }
    $(containerNode).find('.HelpTopic').attr('role', 'button');
  }, [onPopupOpen]);

  const initializeProps = useDeepMemo({
    supportOptions,
    userContext,
    helpModel,
    apexConfig,
    currentLocale,
    onRailViewOpen,
    onRailViewClose,
    onPopupOpen: onHelpPopupOpenWithA11yFix,
    onPopupClose,
    renderApexHelpSuggestions,
    renderSearchInputIcon,
  });

  useEffect(() => {
    activity.trace(`helpmodel: SupportModals initApexPlatform, ${helpModel && helpModel.BaseHelpUrl}`);
    // Once element has been loaded into DOM, initialize apex
    initApexPlatform(initializeProps, activity.trace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeProps]);

  const currentAccount = userContext.currentAccount || userContext.CurrentAccount;

  return (
    <React.Fragment>
      {showRequestCall &&
        <RequestCallSupport
          {...props}
          i18n={i18n}
          currentAccount={currentAccount}
          supportOptions={supportOptions}
          permissions={permissions}
          featureName={featureName}
          portalClassName={callPortalClassName}
        />
      }
      {showRequestChat &&
        <RequestChatSupport
          {...props}
          activity={activity}
          i18n={i18n}
          currentAccount={currentAccount}
          permissions={permissions}
          featureName={featureName}
          supportOptions={supportOptions}
          portalClassName={chatPortalClassName}
          isCopilotEnabled={isCopilotEnabled}
        />
      }
    </React.Fragment>
  );
};

SupportModalsInternal.propTypes = {
  supportOptions: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  userContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      AccountNumber: PropTypes.string,
      ShortName: PropTypes.string,
      Name: PropTypes.string,
    }),
    CurrentCustomer: PropTypes.shape({
      Id: PropTypes.number,
      Name: PropTypes.string,
    }),
    CurrentUser: PropTypes.shape({
      Market: PropTypes.string,
      Name: PropTypes.string,
      Email: PropTypes.string,
    }),
  }).isRequired,
  onRailViewOpen: PropTypes.func,
  onRailViewClose: PropTypes.func,
  onPopupOpen: PropTypes.func,
  onPopupClose: PropTypes.func,
  helpModel: PropTypes.shape({
    BaseHelpUrl: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isCopilotEnabled: PropTypes.bool,
  currentLocale: PropTypes.string.isRequired,
  featureName: PropTypes.string,
  chatPortalClassName: PropTypes.string,
  callPortalClassName: PropTypes.string,
  showRequestCall: PropTypes.bool,
  showRequestChat: PropTypes.bool,
  apexConfig: PropTypes.shape({
    Project: PropTypes.string,
    EndPoint: PropTypes.string,
    SourceEndPoint: PropTypes.string,
  }),
  activity: PropTypes.shape({
    trace: PropTypes.func,
  }),
  renderApexHelpSuggestions: PropTypes.func,
  renderSearchInputIcon: PropTypes.func,
};

SupportModalsInternal.defaultProps = {
  featureName: undefined,
  chatPortalClassName: undefined,
  callPortalClassName: undefined,
  showRequestCall: true,
  showRequestChat: true,
  apexConfig: {},
  activity: { trace: _.noop },
  onRailViewOpen: () => { },
  onRailViewClose: () => { },
  onPopupOpen: () => { },
  onPopupClose: () => { },
  renderApexHelpSuggestions: undefined,
  renderSearchInputIcon: undefined,
  isCopilotEnabled: false,
};

export const SupportModals = withActivityContext(SupportModalsInternal);
